import { isAdmin } from '@/constants/roles';
import {
    AppApi,
    ProjectFolder,
    ThreadFolderAssignment
} from '@/customApiClients/appApiClient/Api';
import { buildShareUrl } from '@/lib/share-link';
import { cn } from '@/lib/utils';
import { size } from 'lodash';
import { FolderOpen, Plus } from 'lucide-react';
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    ClientError,
    ThreadHistory,
    threadHistoryState,
    useChatInteract,
    useChatMessages,
    useChatSession
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuItem
} from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';

import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';

import { Translator } from '../i18n';
import FolderContextMenu from './FolderContextMenu';
import { ProjectDialogs } from './ProjectDialogs';
import { ProjectFolderItem } from './ProjectFolderItem';
import { SidebarListSkeleton } from './SidebarListSkeleton';
import ThreadContextMenu from './ThreadContextMenu';
import { TimeGroupedThreadList } from './TimeGroupedThreadList';
import { useDragDropHandlers } from './useDragDropHandlers';
import { useProjectManagement } from './useProjectManagement';

// Use the generated types from the API client
type IProjectFolder = ProjectFolder;
type IThreadFolderAssignment = ThreadFolderAssignment;

export interface PinnedThreadListHandlers {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    isDragOver: boolean;
}

interface ThreadListProps {
    threadHistory?: ThreadHistory;
    error?: string;
    isFetching: boolean;
    isLoadingMore: boolean;
    onThreadPinned?: () => void;
    onPinnedHandlersReady?: (handlers: PinnedThreadListHandlers) => void;
}

export function ThreadList({
    threadHistory,
    error,
    isFetching,
    isLoadingMore,
    onThreadPinned,
    onPinnedHandlersReady
}: ThreadListProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const userIsAdmin = isAdmin(user?.metadata?.organization_roles);
    const { idToResume } = useChatSession();
    const { clear } = useChatInteract();
    const { threadId: currentThreadId } = useChatMessages();
    const [threadIdToDelete, setThreadIdToDelete] = useState<string>();
    const [threadIdToRename, setThreadIdToRename] = useState<string>();
    const [threadNewName, setThreadNewName] = useState<string>();
    const [threadIdToShare, setThreadIdToShare] = useState<string>();
    const [shareUrl, setShareUrl] = useState<string>();
    const setThreadHistory = useSetRecoilState(threadHistoryState);
    const chainlitApiClient = useContext(ChainlitContext);

    // Initialize our generated API client (memoized to prevent infinite loops)
    const appApiClient = useMemo(
        () =>
            new AppApi({
                baseUrl: window.location.origin, // Use current host
                baseApiParams: {
                    credentials: 'include' // Include cookies for authentication
                }
            }),
        []
    );

    // Helper function to convert ProjectFolder to IProjectFolder
    const convertToIProjectFolder = (
        folder: ProjectFolder
    ): IProjectFolder => ({
        id: folder.id,
        name: folder.name,
        userId: folder.userId,
        parentId: folder.parentId || undefined,
        level: folder.level,
        expanded: folder.expanded,
        createdAt: folder.createdAt
    });

    // Helper function to calculate folder levels and build hierarchy
    const buildFolderHierarchy = (
        folders: ProjectFolder[]
    ): IProjectFolder[] => {
        return folders.map(convertToIProjectFolder);
    };

    // Backend-powered folder state
    const [folders, setFolders] = useState<IProjectFolder[]>([]);
    const [assignments, setAssignments] = useState<IThreadFolderAssignment[]>(
        []
    );

    // Dialog states
    const [addFolderOpen, setAddFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [
        threadToAssignAfterFolderCreation,
        setThreadToAssignAfterFolderCreation
    ] = useState<string>();
    const [folderIdToRename, setFolderIdToRename] = useState<string>();
    const [folderNewName, setFolderNewName] = useState('');
    const [folderIdToDelete, setFolderIdToDelete] = useState<string>();
    const [subfolderParentId, setSubfolderParentId] = useState<string>();
    const [subfolderName, setSubfolderName] = useState('');

    // Multi-select thread dialog states
    const [multiSelectFolderId, setMultiSelectFolderId] = useState<string>();
    const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(
        new Set()
    );

    // Drag states
    const [dragOverFolderId, setDragOverFolderId] = useState<string>();
    const [dragFolderId, setDragFolderId] = useState<string>();
    const [showDropZone, setShowDropZone] = useState<boolean>(false);
    const [dragOverRoot, setDragOverRoot] = useState(false);
    const [dragOverTimeGroups, setDragOverTimeGroups] = useState(false);
    const [dragOverPinnedArea, setDragOverPinnedArea] = useState(false);
    const [draggedThreadId, setDraggedThreadId] = useState<string>();

    // Touch drag states for mobile
    const [touchDraggedId, setTouchDraggedId] = useState<string>();
    const [touchDragType, setTouchDragType] = useState<'thread' | 'folder'>();
    const [touchDragElement, setTouchDragElement] = useState<HTMLElement>();
    const [touchPosition, setTouchPosition] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const [touchDragWidth, setTouchDragWidth] = useState<number | null>(null);
    const [touchHoldTimer, setTouchHoldTimer] = useState<NodeJS.Timeout | null>(
        null
    );

    // Dropdown visibility states
    const [openDropdownId, setOpenDropdownId] = useState<string>();
    const [openThreadDropdownId, setOpenThreadDropdownId] = useState<string>();

    // Context menu state
    const [contextMenuPosition, setContextMenuPosition] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const [contextMenuType, setContextMenuType] = useState<
        'thread' | 'folder' | null
    >(null);
    const [contextMenuTargetId, setContextMenuTargetId] = useState<
        string | null
    >(null);

    const [isDragging, setIsDragging] = useState(false);
    const [isInScrollZone, setIsInScrollZone] = useState<
        'top' | 'bottom' | false
    >(false);
    const [isAtTop, setIsAtTop] = useState(true);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const hasInitiallyLoadedFoldersRef = useRef(false);
    const loadFoldersRef = useRef<(showSpinner?: boolean) => Promise<void>>();
    const projectsSectionRef = useRef<HTMLDivElement>(null);
    const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Load folders and assignments from backend
    // Use ref pattern to stabilize the function reference
    loadFoldersRef.current = async (showSpinner = true) => {
        try {
            // Capture current expanded states before loading
            // Use the callback form of setFolders to get the current state
            let currentExpandedStates = new Map<string, boolean>();
            setFolders((prevFolders) => {
                prevFolders.forEach((folder) => {
                    currentExpandedStates.set(folder.id, folder.expanded);
                });
                return prevFolders; // Don't actually update, just capture state
            });

            const response =
                await appApiClient.project.listFoldersProjectFoldersGet();
            const data = response.data;

            if (data && data.folders) {
                const hierarchicalFolders = buildFolderHierarchy(data.folders);

                // Preserve expanded states from before the reload
                // This prevents folders from collapsing when we reload after operations
                const foldersWithPreservedState = hierarchicalFolders.map(
                    (folder) => {
                        const wasExpanded = currentExpandedStates.get(
                            folder.id
                        );
                        // If we had a local state for this folder, prefer it over backend state
                        // This handles the case where backend might have stale data
                        if (
                            wasExpanded !== undefined &&
                            wasExpanded !== folder.expanded
                        ) {
                            return { ...folder, expanded: wasExpanded };
                        }
                        return folder;
                    }
                );

                // Only update if different to prevent unnecessary re-renders
                const currentFoldersJson = JSON.stringify(folders);
                const newFoldersJson = JSON.stringify(
                    foldersWithPreservedState
                );

                if (currentFoldersJson !== newFoldersJson) {
                    setFolders(foldersWithPreservedState);
                }
            } else {
                if (folders.length > 0) {
                    setFolders([]);
                }
            }

            if (data && data.assignments) {
                const frontendAssignments: IThreadFolderAssignment[] =
                    data.assignments.map((assignment: any) => ({
                        threadId: assignment.threadId,
                        folderId: assignment.folderId
                    }));

                // Only update if different to prevent unnecessary re-renders
                const currentAssignmentsJson = JSON.stringify(assignments);
                const newAssignmentsJson = JSON.stringify(frontendAssignments);

                if (currentAssignmentsJson !== newAssignmentsJson) {
                    setAssignments(frontendAssignments);
                }
            } else {
                if (assignments.length > 0) {
                    setAssignments([]);
                }
            }
        } catch (err) {
            console.error('Failed to load folders:', err);
        }
    };

    // Stable reference to loadFolders that won't change between renders
    const loadFolders = useCallback(async (showSpinner = true) => {
        if (loadFoldersRef.current) {
            console.log('[ThreadList] loadFolders called');
            await loadFoldersRef.current(showSpinner);
        }
    }, []);

    // Initial load only - prevent reloading on every render
    useEffect(() => {
        if (!hasInitiallyLoadedFoldersRef.current) {
            console.log('[ThreadList] Initial folders load triggered');
            hasInitiallyLoadedFoldersRef.current = true;
            loadFolders();
        }
    }, [loadFolders]);

    // Auto-scroll when dragging towards top or bottom
    useEffect(() => {
        if (!isDragging) {
            // Clear any existing auto-scroll interval
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
                autoScrollIntervalRef.current = null;
            }
            setIsInScrollZone(false);
            return;
        }

        const scrollContainer = document.querySelector(
            '.flex-1.min-h-0.overflow-y-auto'
        );
        if (!scrollContainer) return;

        // Function to check scroll position
        const updateScrollPosition = () => {
            const scrollTop = scrollContainer.scrollTop;
            const scrollHeight = scrollContainer.scrollHeight;
            const clientHeight = scrollContainer.clientHeight;
            const atTop = scrollTop <= 5;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 120;

            setIsAtTop(atTop);
            setIsAtBottom(atBottom);
        };

        // Initial check when dragging starts
        updateScrollPosition();

        const handleMouseMove = (e: MouseEvent) => {
            const rect = scrollContainer.getBoundingClientRect();
            const scrollZone = 120; // Pixels from edge to trigger scroll
            const scrollSpeed = 5; // Pixels per interval

            // Update scroll position
            updateScrollPosition();

            // Check if mouse is in the scroll zones
            const distanceFromTop = e.clientY - rect.top;
            const distanceFromBottom = rect.bottom - e.clientY;

            // Get current scroll state
            const scrollTop = scrollContainer.scrollTop;
            const scrollHeight = scrollContainer.scrollHeight;
            const clientHeight = scrollContainer.clientHeight;
            const atTop = scrollTop <= 5;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 120;

            if (distanceFromTop < scrollZone && distanceFromTop > 0 && !atTop) {
                setIsInScrollZone('top');
                // Start auto-scrolling up
                if (!autoScrollIntervalRef.current) {
                    autoScrollIntervalRef.current = setInterval(() => {
                        scrollContainer.scrollBy({
                            top: -scrollSpeed,
                            behavior: 'auto'
                        });
                    }, 16); // ~60fps
                }
            } else if (
                distanceFromBottom < scrollZone &&
                distanceFromBottom > 0 &&
                !atBottom
            ) {
                setIsInScrollZone('bottom');
                // Start auto-scrolling down
                if (!autoScrollIntervalRef.current) {
                    autoScrollIntervalRef.current = setInterval(() => {
                        scrollContainer.scrollBy({
                            top: scrollSpeed,
                            behavior: 'auto'
                        });
                    }, 16); // ~60fps
                }
            } else {
                setIsInScrollZone(false);
                // Stop auto-scrolling
                if (autoScrollIntervalRef.current) {
                    clearInterval(autoScrollIntervalRef.current);
                    autoScrollIntervalRef.current = null;
                }
            }
        };

        const handleScroll = () => {
            updateScrollPosition();
        };

        document.addEventListener('mousemove', handleMouseMove);
        scrollContainer.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            scrollContainer.removeEventListener('scroll', handleScroll);
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
                autoScrollIntervalRef.current = null;
            }
        };
    }, [isDragging]);

    // Context menu handlers
    const handleThreadContextMenu = useCallback(
        (e: React.MouseEvent, threadId: string) => {
            e.preventDefault();
            setContextMenuPosition({ x: e.clientX, y: e.clientY });
            setContextMenuType('thread');
            setContextMenuTargetId(threadId);
            setOpenThreadDropdownId(threadId);
        },
        []
    );

    const handleFolderContextMenu = useCallback(
        (e: React.MouseEvent, folderId: string) => {
            e.preventDefault();
            setContextMenuPosition({ x: e.clientX, y: e.clientY });
            setContextMenuType('folder');
            setContextMenuTargetId(folderId);
            setOpenDropdownId(folderId);
        },
        []
    );

    // Close context menu on outside click or escape
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (contextMenuPosition) {
                const target = e.target as Element;
                if (
                    target.closest('[role="menu"]') ||
                    target.closest('[data-radix-menu-content]') ||
                    target.closest('[data-context-menu]')
                ) {
                    return;
                }
                setContextMenuPosition(null);
                setContextMenuType(null);
                setContextMenuTargetId(null);
                setOpenThreadDropdownId(undefined);
                setOpenDropdownId(undefined);
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && contextMenuPosition) {
                setContextMenuPosition(null);
                setContextMenuType(null);
                setContextMenuTargetId(null);
                setOpenThreadDropdownId(undefined);
                setOpenDropdownId(undefined);
            }
        };

        const handleContextMenu = (e: MouseEvent) => {
            if (contextMenuPosition) {
                const target = e.target as Element;
                if (!target.closest('[data-context-menu-trigger]')) {
                    setContextMenuPosition(null);
                    setContextMenuType(null);
                    setContextMenuTargetId(null);
                    setOpenThreadDropdownId(undefined);
                    setOpenDropdownId(undefined);
                }
            }
        };

        if (contextMenuPosition) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('contextmenu', handleContextMenu);
            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('keydown', handleEscape);
                document.removeEventListener('contextmenu', handleContextMenu);
            };
        }
    }, [contextMenuPosition]);

    // Safety: Clear drag states on global dragend/drop to prevent stuck indicators
    useEffect(() => {
        const clearDragStates = () => {
            setDragOverFolderId(undefined);
            setDraggedThreadId(undefined);
            setDragOverTimeGroups(false);
            setDragOverRoot(false);
        };

        // Listen for dragend and drop events globally
        document.addEventListener('dragend', clearDragStates);
        document.addEventListener('drop', clearDragStates);

        return () => {
            document.removeEventListener('dragend', clearDragStates);
            document.removeEventListener('drop', clearDragStates);
        };
    }, []);

    // Thread assignment function
    const assignThreadToFolder = useCallback(
        async (folderId: string, threadId: string, skipReload = false) => {
            try {
                await appApiClient.project.assignThreadToFolderProjectThreadFolderAssignmentPost(
                    {
                        thread_id: threadId,
                        folder_id: folderId
                    }
                );
                // Always reload to get correct backend data, but skip spinner for drag operations
                await loadFolders(!skipReload);
            } catch (err) {
                console.error('Failed to assign thread:', err);
                // On error, reload with spinner to show something is happening
                await loadFolders(true);
            }
        },
        [appApiClient, loadFolders]
    );

    const removeThreadFromFolder = useCallback(
        async (threadId: string, skipReload = false) => {
            try {
                await appApiClient.project.assignThreadToFolderProjectThreadFolderAssignmentPost(
                    {
                        thread_id: threadId,
                        folder_id: undefined
                    }
                );
                // Always reload to get correct backend data, but skip spinner for drag operations
                await loadFolders(!skipReload);
            } catch (err) {
                console.error('Failed to remove thread:', err);
                // On error, reload with spinner to show something is happening
                await loadFolders(true);
            }
        },
        [appApiClient, loadFolders]
    );

    const pinThread = useCallback(
        async (threadId: string) => {
            try {
                await chainlitApiClient.updateThread(threadId, {
                    isPinned: true
                });
                onThreadPinned?.();

                // Notify thread page to reload
                window.dispatchEvent(
                    new CustomEvent('thread-pin-changed', {
                        detail: { threadId }
                    })
                );
            } catch (err) {
                console.error('Failed to pin thread:', err);
            }
        },
        [chainlitApiClient, onThreadPinned]
    );

    // Initialize project management hook
    const {
        createFolder: createFolderOperation,
        createSubfolder,
        renameFolder,
        deleteFolder,
        moveFolder,
        toggleFolder,
        moveFolderToParent,
        moveFolderToRoot
    } = useProjectManagement({
        appApiClient,
        folders: folders as any,
        setFolders: setFolders as any,
        loadFolders,
        assignThreadToFolder
    });

    // Initialize drag and drop handlers
    const dragDropHandlers = useDragDropHandlers({
        isMobile,
        folders,
        folderedThreadIds: useMemo(() => {
            const set = new Set<string>();
            assignments.forEach((assignment) => set.add(assignment.threadId));
            return set;
        }, [assignments]),
        threadHistory,
        assignThreadToFolder,
        removeThreadFromFolder,
        moveFolderToParent,
        moveFolderToRoot,
        toggleFolder,
        loadFolders,
        pinThread,
        setDraggedThreadId,
        setDragOverTimeGroups,
        setDragOverPinnedArea,
        setDragFolderId,
        setShowDropZone,
        setDragOverFolderId,
        setTouchDraggedId,
        setTouchDragType,
        setTouchDragElement,
        setIsDragging,
        setDragOverRoot,
        setTouchPosition,
        setTouchDragWidth,
        touchHoldTimer,
        setTouchHoldTimer,
        dragOverFolderId,
        dragFolderId,
        draggedThreadId,
        touchDraggedId,
        touchDragType,
        dragOverPinnedArea
    });

    // Export pinned area handlers for PinnedToOrgThreadList
    const pinnedThreadListHandlers: PinnedThreadListHandlers = useMemo(
        () => ({
            onDragOver: dragDropHandlers.handlePinnedAreaDragOver,
            onDragLeave: dragDropHandlers.handlePinnedAreaDragLeave,
            onDrop: dragDropHandlers.handlePinnedAreaDrop,
            isDragOver: dragOverPinnedArea
        }),
        [dragDropHandlers, dragOverPinnedArea]
    );

    // Notify parent when handlers are ready
    useEffect(() => {
        onPinnedHandlersReady?.(pinnedThreadListHandlers);
    }, [pinnedThreadListHandlers, onPinnedHandlersReady]);

    const sortedTimeGroupKeys = useMemo(() => {
        const groups = threadHistory?.timeGroupedThreads;
        if (!groups) return [];
        const fixedOrder = [
            'Today',
            'Yesterday',
            'Previous 7 days',
            'Previous 30 days'
        ];
        // Nyeste createdAt i en gruppe (epoch ms). Bruges til kronologisk
        // sortering af "Måned År"-grupper, locale-uafhængigt.
        const groupTime = (key: string) =>
            (groups[key] ?? []).reduce((max, t) => {
                const ts = new Date(t.createdAt).getTime();
                return Number.isNaN(ts) ? max : Math.max(max, ts);
            }, 0);

        return Object.keys(groups).sort((a, b) => {
            const aIndex = fixedOrder.indexOf(a);
            const bIndex = fixedOrder.indexOf(b);
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return groupTime(b) - groupTime(a); // nyeste først
        });
    }, [threadHistory?.timeGroupedThreads]);

    // Helper: set of thread ids assigned to any folder (used to hide from time groups list)
    const folderedThreadIds = useMemo(() => {
        const set = new Set<string>();
        assignments.forEach((assignment) => set.add(assignment.threadId));
        return set;
    }, [assignments]);

    // Helper: get folders in tree order (parents before children)
    const getFoldersInTreeOrder = useMemo(() => {
        const result: IProjectFolder[] = [];
        const addFoldersAtLevel = (
            parentId: string | undefined,
            level: number
        ) => {
            const foldersAtLevel = folders
                .filter((f) => f.parentId === parentId && f.level === level)
                .sort((a, b) => a.name.localeCompare(b.name));

            for (const folder of foldersAtLevel) {
                result.push(folder);
                if (folder.expanded) {
                    addFoldersAtLevel(folder.id, level + 1);
                }
            }
        };
        addFoldersAtLevel(undefined, 0);
        return result;
    }, [folders]);

    // Helper: check if a folder is a descendant of another folder
    const isDescendantOf = useCallback(
        (parentId: string, potentialDescendantId: string): boolean => {
            if (!folders) return false;

            const findParentChain = (folderId: string): string[] => {
                const folder = folders.find((f) => f.id === folderId);
                if (!folder || !folder.parentId) return [];
                return [folder.parentId, ...findParentChain(folder.parentId)];
            };

            return findParentChain(potentialDescendantId).includes(parentId);
        },
        [folders]
    );

    // Get threads for a specific folder
    const getThreadsForFolder = useCallback(
        (folderId: string) => {
            const threadIds = assignments
                .filter((a) => a.folderId === folderId)
                .map((a) => a.threadId);

            return threadIds
                .map((id) => threadHistory?.threads?.find((t) => t.id === id))
                .filter(Boolean);
        },
        [assignments, threadHistory?.threads]
    );

    // Get all descendant folder IDs (including the folder itself)
    const getDescendantFolderIds = useCallback(
        (folderId: string): string[] => {
            if (!folders) return [folderId];

            const descendants = [folderId];
            const addChildren = (parentId: string) => {
                const children = folders.filter((f) => f.parentId === parentId);
                children.forEach((child) => {
                    descendants.push(child.id);
                    addChildren(child.id);
                });
            };

            addChildren(folderId);
            return descendants;
        },
        [folders]
    );

    // Get threads for a folder including all subfolders
    const getThreadsForFolderIncludingSubfolders = useCallback(
        (folderId: string) => {
            const allFolderIds = getDescendantFolderIds(folderId);
            const threadIds = assignments
                .filter((a) => allFolderIds.includes(a.folderId))
                .map((a) => a.threadId);

            return threadIds
                .map((id) => threadHistory?.threads?.find((t) => t.id === id))
                .filter(Boolean);
        },
        [assignments, threadHistory?.threads, getDescendantFolderIds]
    );

    // Multi-select thread functions
    const openMultiSelectDialog = useCallback((folderId: string) => {
        setMultiSelectFolderId(folderId);
        setSelectedThreadIds(new Set());
    }, []);

    const closeMultiSelectDialog = useCallback(() => {
        setMultiSelectFolderId(undefined);
        setSelectedThreadIds(new Set());
    }, []);

    const toggleThreadSelection = useCallback((threadId: string) => {
        setSelectedThreadIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(threadId)) {
                newSet.delete(threadId);
            } else {
                newSet.add(threadId);
            }
            return newSet;
        });
    }, []);

    const selectAllThreads = useCallback(() => {
        const availableThreadIds = new Set<string>();
        sortedTimeGroupKeys.forEach((group) => {
            const items = threadHistory?.timeGroupedThreads?.[group] || [];
            items
                .filter((thread) => !folderedThreadIds.has(thread.id))
                .forEach((thread) => availableThreadIds.add(thread.id));
        });
        setSelectedThreadIds(availableThreadIds);
    }, [
        sortedTimeGroupKeys,
        threadHistory?.timeGroupedThreads,
        folderedThreadIds
    ]);

    const deselectAllThreads = useCallback(() => {
        setSelectedThreadIds(new Set());
    }, []);

    const assignSelectedThreads = useCallback(async () => {
        if (!multiSelectFolderId || selectedThreadIds.size === 0) return;

        try {
            // Assign all threads with skipReload to avoid multiple spinners
            await Promise.all(
                Array.from(selectedThreadIds).map((threadId) =>
                    assignThreadToFolder(multiSelectFolderId, threadId, true)
                )
            );
            closeMultiSelectDialog();
        } catch (err) {
            console.error('Failed to assign selected threads:', err);
        }
    }, [
        multiSelectFolderId,
        selectedThreadIds,
        assignThreadToFolder,
        closeMultiSelectDialog
    ]);

    // Handle adding a thread to project (direct assignment)
    const handleAddToProject = useCallback(
        (threadId: string, folderId: string) => {
            assignThreadToFolder(folderId, threadId);
        },
        [assignThreadToFolder]
    );

    // Create folder wrapper
    const createFolder = useCallback(async () => {
        if (!newFolderName.trim()) return;

        try {
            await createFolderOperation(
                newFolderName,
                threadToAssignAfterFolderCreation
            );
            setNewFolderName('');
            setAddFolderOpen(false);
            setThreadToAssignAfterFolderCreation(undefined);
        } catch (err) {
            console.error('Failed to create folder:', err);
            setThreadToAssignAfterFolderCreation(undefined);
        }
    }, [
        newFolderName,
        threadToAssignAfterFolderCreation,
        createFolderOperation
    ]);

    // Create subfolder wrapper
    const handleCreateSubfolder = useCallback(async () => {
        if (!subfolderParentId || !subfolderName.trim()) return;

        try {
            await createSubfolder(subfolderParentId, subfolderName);
            setSubfolderName('');
            setSubfolderParentId(undefined);
        } catch (err) {
            console.error('Failed to create subfolder:', err);
        }
    }, [subfolderParentId, subfolderName, createSubfolder]);

    // Thread management handlers
    const handleDeleteThread = async () => {
        if (!threadIdToDelete) return;

        const isDeletingCurrentThread =
            threadIdToDelete === idToResume ||
            threadIdToDelete === currentThreadId;

        if (isDeletingCurrentThread) {
            clear();
            navigate('/');
        }

        setThreadHistory((prev) => ({
            ...prev,
            threads: prev?.threads?.filter((t) => t.id !== threadIdToDelete)
        }));

        try {
            await chainlitApiClient.deleteThread(threadIdToDelete);
            if (!isDeletingCurrentThread) {
                navigate('/');
            }
        } catch (err) {
            console.error('Failed to delete thread:', err);
            toast.error('Failed to delete thread');
        }
    };

    const handleRenameThread = async () => {
        if (!threadIdToRename || !threadNewName) return;

        try {
            await chainlitApiClient.renameThread(
                threadIdToRename,
                threadNewName
            );
            setThreadNewName(undefined);
            setThreadIdToRename(undefined);
            setThreadHistory((prev) => {
                const next = {
                    ...prev,
                    threads: prev?.threads ? [...prev.threads] : undefined
                };
                const threadIndex = next.threads?.findIndex(
                    (t) => t.id === threadIdToRename
                );
                if (typeof threadIndex === 'number' && next.threads) {
                    next.threads[threadIndex] = {
                        ...next.threads[threadIndex],
                        name: threadNewName
                    };
                }
                return next;
            });
        } catch (err) {
            console.error('Failed to rename thread:', err);
        }
    };

    const handlePinToOrg = async (threadId: string) => {
        try {
            await chainlitApiClient.updateThread(threadId, { isPinned: true });

            // Remove thread from the current list
            setThreadHistory((prev) => ({
                ...prev,
                threads: prev?.threads?.filter((t) => t.id !== threadId)
            }));

            // Trigger callback to reload pinned threads list
            onThreadPinned?.();

            // Notify thread page to reload
            window.dispatchEvent(
                new CustomEvent('thread-pin-changed', {
                    detail: { threadId }
                })
            );

            toast.success('Thread pinned to organization');
        } catch (err) {
            console.error('Failed to pin thread:', err);
            toast.error('Failed to pin thread');
        }
    };

    const handleShareThread = async (threadId: string) => {
        try {
            // Call backend to create share link
            const response = await fetch(
                `${window.location.origin}/project/thread/${threadId}/share`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to create share link');
            }

            const data = await response.json();

            // Store the share URL and open dialog. When embedded inside a host
            // (BIG), rewrite the bare chat-origin link to a host deep link the
            // host can receive and load into its embedded chat iframe.
            setShareUrl(buildShareUrl(data.token, data.shareUrl));
            setThreadIdToShare(threadId);
        } catch (error) {
            console.error('Error creating share link:', error);
            toast.error('Failed to create share link');
        }
    };

    const handleCopyShareUrl = async () => {
        if (shareUrl) {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Share link copied to clipboard');
        }
    };

    const getTimeGroupLabel = (group: string) => {
        const labels = {
            Today: <Translator path="threadHistory.sidebar.timeframes.today" />,
            Yesterday: (
                <Translator path="threadHistory.sidebar.timeframes.yesterday" />
            ),
            'Previous 7 days': (
                <Translator path="threadHistory.sidebar.timeframes.previous7days" />
            ),
            'Previous 30 days': (
                <Translator path="threadHistory.sidebar.timeframes.previous30days" />
            )
        };
        return labels[group as keyof typeof labels] || group;
    };

    // Check if the currently dragged folder can be moved to root
    const canMoveToRoot = useMemo(() => {
        if (!dragFolderId) return false;
        const draggedFolder = folders.find((f) => f.id === dragFolderId);
        return (
            draggedFolder &&
            draggedFolder.parentId !== undefined &&
            draggedFolder.parentId !== null
        );
    }, [dragFolderId, folders]);

    if (isFetching || (!threadHistory?.timeGroupedThreads && isLoadingMore)) {
        return <SidebarListSkeleton />;
    }

    if (error) {
        return (
            <Alert variant="error" className="m-3">
                {error}
            </Alert>
        );
    }

    return (
        <>
            {/* Drag Indicator for Mobile - follows finger */}
            {isMobile &&
                isDragging &&
                touchDraggedId &&
                touchPosition &&
                (() => {
                    // Find the dragged item
                    let draggedItem: {
                        name: string;
                        type: 'thread' | 'folder';
                    } | null = null;

                    if (touchDragType === 'folder') {
                        const folder = folders.find(
                            (f) => f.id === touchDraggedId
                        );
                        if (folder) {
                            draggedItem = { name: folder.name, type: 'folder' };
                        }
                    } else if (touchDragType === 'thread') {
                        const thread = threadHistory?.threads?.find(
                            (t: any) => t.id === touchDraggedId
                        );
                        if (thread) {
                            draggedItem = {
                                name: thread.name || 'Untitled',
                                type: 'thread'
                            };
                        }
                    }

                    if (!draggedItem) return null;

                    return (
                        <div
                            className="z-9999 fixed pointer-events-none"
                            style={{
                                left: `${touchPosition.x}px`,
                                top: `${touchPosition.y}px`,
                                transform:
                                    'translate(-50%, -50%) rotate(-3deg)',
                                width: touchDragWidth
                                    ? `${touchDragWidth}px`
                                    : 'auto'
                            }}
                        >
                            <div className="bg-sidebar opacity-90 shadow-2xl px-3 py-2 border-2 border-border rounded-lg scale-110">
                                <div className="flex items-center gap-2 min-w-0">
                                    {draggedItem.type === 'folder' && (
                                        <FolderOpen className="shrink-0 w-4 h-4 text-foreground" />
                                    )}
                                    <span className="font-normal text-foreground truncate">
                                        {draggedItem.name}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })()}

            {/* All Dialogs */}
            <ProjectDialogs
                addFolderOpen={addFolderOpen}
                setAddFolderOpen={setAddFolderOpen}
                newFolderName={newFolderName}
                setNewFolderName={setNewFolderName}
                onCreateFolder={createFolder}
                threadToAssignAfterFolderCreation={
                    threadToAssignAfterFolderCreation
                }
                setThreadToAssignAfterFolderCreation={
                    setThreadToAssignAfterFolderCreation
                }
                subfolderParentId={subfolderParentId}
                setSubfolderParentId={setSubfolderParentId}
                subfolderName={subfolderName}
                setSubfolderName={setSubfolderName}
                onCreateSubfolder={handleCreateSubfolder}
                folderIdToRename={folderIdToRename}
                setFolderIdToRename={setFolderIdToRename}
                folderNewName={folderNewName}
                setFolderNewName={setFolderNewName}
                onRenameFolder={async () => {
                    await renameFolder(folderIdToRename!, folderNewName);
                    setFolderIdToRename(undefined);
                }}
                folderIdToDelete={folderIdToDelete}
                setFolderIdToDelete={setFolderIdToDelete}
                onDeleteFolder={() => deleteFolder(folderIdToDelete!)}
                multiSelectFolderId={multiSelectFolderId}
                onCloseMultiSelectDialog={closeMultiSelectDialog}
                folders={folders}
                threadHistory={threadHistory!}
                sortedTimeGroupKeys={sortedTimeGroupKeys}
                folderedThreadIds={folderedThreadIds}
                getTimeGroupLabel={getTimeGroupLabel}
                selectedThreadIds={selectedThreadIds}
                onToggleThreadSelection={toggleThreadSelection}
                onSelectAllThreads={selectAllThreads}
                onDeselectAllThreads={deselectAllThreads}
                onAssignSelectedThreads={assignSelectedThreads}
                isMobile={isMobile}
                threadIdToDelete={threadIdToDelete}
                setThreadIdToDelete={setThreadIdToDelete}
                onDeleteThread={handleDeleteThread}
                threadIdToRename={threadIdToRename}
                setThreadIdToRename={setThreadIdToRename}
                threadNewName={threadNewName}
                setThreadNewName={setThreadNewName}
                onRenameThread={handleRenameThread}
                threadIdToShare={threadIdToShare}
                setThreadIdToShare={(id) => {
                    setThreadIdToShare(id);
                    if (!id) setShareUrl(undefined);
                }}
                shareUrl={shareUrl}
                onCopyShareUrl={handleCopyShareUrl}
            />

            <TooltipProvider>
                {/* Scroll zone indicators - fixed at top and bottom of scroll container */}
                {isDragging &&
                    (() => {
                        const scrollContainer = document.querySelector(
                            '.flex-1.min-h-0.overflow-y-auto'
                        );
                        if (!scrollContainer) return null;

                        const rect = scrollContainer.getBoundingClientRect();
                        const showTop = !isAtTop;
                        const showBottom = !isAtBottom;

                        return createPortal(
                            <>
                                {/* Top scroll zone */}
                                {showTop && (
                                    <div
                                        className="z-100 fixed pointer-events-none"
                                        style={{
                                            top: `${rect.top}px`,
                                            left: `${rect.left}px`,
                                            width: `${rect.width}px`,
                                            height: '120px'
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                'relative h-full transition-all duration-300',
                                                isInScrollZone === 'top'
                                                    ? 'bg-linear-to-b from-blue-500/8 via-blue-500/4 to-transparent'
                                                    : 'bg-linear-to-b from-gray-500/5 via-gray-500/2 to-transparent'
                                            )}
                                        >
                                            <div className="top-3 absolute inset-x-0 flex flex-col items-center animate-[bounce_2s_ease-in-out_infinite]">
                                                <div
                                                    className={cn(
                                                        'text-xl transition-colors duration-300',
                                                        isInScrollZone === 'top'
                                                            ? 'text-blue-400/60'
                                                            : 'text-gray-400/40'
                                                    )}
                                                >
                                                    ↑
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Bottom scroll zone */}
                                {showBottom && (
                                    <div
                                        className="z-100 fixed pointer-events-none"
                                        style={{
                                            top: `${rect.bottom - 120}px`,
                                            left: `${rect.left}px`,
                                            width: `${rect.width}px`,
                                            height: '120px'
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                'relative h-full transition-all duration-300',
                                                isInScrollZone === 'bottom'
                                                    ? 'bg-linear-to-t from-blue-500/8 via-blue-500/4 to-transparent'
                                                    : 'bg-linear-to-t from-gray-500/5 via-gray-500/2 to-transparent'
                                            )}
                                        >
                                            <div className="bottom-1 absolute inset-x-0 flex flex-col items-center animate-[bounce_2s_ease-in-out_infinite]">
                                                <div
                                                    className={cn(
                                                        'text-xl transition-colors duration-300',
                                                        isInScrollZone ===
                                                            'bottom'
                                                            ? 'text-blue-400/60'
                                                            : 'text-gray-400/40'
                                                    )}
                                                >
                                                    ↓
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>,
                            document.body
                        );
                    })()}

                {/* Projects / Folders Group */}
                <div ref={projectsSectionRef}>
                    <SidebarGroup
                        className="top-0 z-30 p-0"
                        style={{
                            backgroundColor: 'hsl(var(--sidebar-background))'
                        }}
                    >
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <SidebarGroupLabel
                                    data-fluid-hover-ignore
                                    className={cn(
                                        'justify-between pr-1 pl-3 font-normal! text-muted-foreground text-sm tracking-normal'
                                    )}
                                >
                                    Projects
                                    <Button
                                        variant="ghost"
                                        size="icon-compact"
                                        className="[&_svg]:size-5!"
                                        aria-label="New project"
                                        title="New project"
                                        onClick={() => setAddFolderOpen(true)}
                                    >
                                        <Plus />
                                    </Button>
                                </SidebarGroupLabel>

                                {getFoldersInTreeOrder.map((folder) => (
                                    <ProjectFolderItem
                                        key={folder.id}
                                        folder={folder}
                                        folders={folders}
                                        assignments={assignments}
                                        threadHistory={threadHistory}
                                        dragOverFolderId={dragOverFolderId}
                                        dragFolderId={dragFolderId}
                                        draggedThreadId={draggedThreadId}
                                        isMobile={isMobile}
                                        isDragging={isDragging}
                                        touchDraggedId={touchDraggedId}
                                        contextMenuType={contextMenuType}
                                        contextMenuTargetId={
                                            contextMenuTargetId
                                        }
                                        folderIdToRename={folderIdToRename}
                                        threadIdToRename={threadIdToRename}
                                        openDropdownId={openDropdownId}
                                        openThreadDropdownId={
                                            openThreadDropdownId
                                        }
                                        contextMenuPosition={
                                            contextMenuPosition
                                        }
                                        onToggleFolder={toggleFolder}
                                        onFolderDragStart={
                                            dragDropHandlers.handleFolderDragStart
                                        }
                                        onFolderDragEnd={
                                            dragDropHandlers.handleFolderDragEnd
                                        }
                                        onFolderDragOver={
                                            dragDropHandlers.handleFolderDragOver
                                        }
                                        onFolderDragLeave={
                                            dragDropHandlers.handleFolderDragLeave
                                        }
                                        onFolderDrop={
                                            dragDropHandlers.handleFolderDrop
                                        }
                                        onTouchStart={
                                            dragDropHandlers.handleTouchStart
                                        }
                                        onTouchMove={
                                            dragDropHandlers.handleTouchMove
                                        }
                                        onTouchEnd={
                                            dragDropHandlers.handleTouchEnd
                                        }
                                        onFolderContextMenu={
                                            handleFolderContextMenu
                                        }
                                        onThreadContextMenu={
                                            handleThreadContextMenu
                                        }
                                        onThreadDragStart={
                                            dragDropHandlers.handleDragStart
                                        }
                                        onThreadDragEnd={
                                            dragDropHandlers.handleDragEnd
                                        }
                                        onAddSubfolder={(parentId) => {
                                            setSubfolderParentId(parentId);
                                            setSubfolderName('');
                                        }}
                                        onMultiSelectDialog={
                                            openMultiSelectDialog
                                        }
                                        onRenameFolder={(
                                            folderId,
                                            currentName
                                        ) => {
                                            setFolderIdToRename(folderId);
                                            setFolderNewName(currentName);
                                        }}
                                        onDeleteFolder={setFolderIdToDelete}
                                        onMoveFolder={(
                                            folderId,
                                            targetParentId
                                        ) => {
                                            moveFolder(
                                                folderId,
                                                targetParentId
                                            );
                                        }}
                                        onRenameThread={(threadId, name) => {
                                            setThreadIdToRename(threadId);
                                            setThreadNewName(name);
                                        }}
                                        onDeleteThread={setThreadIdToDelete}
                                        onAddToProject={handleAddToProject}
                                        onCreateNewProject={(threadId) => {
                                            if (threadId) {
                                                setThreadToAssignAfterFolderCreation(
                                                    threadId
                                                );
                                            }
                                            setAddFolderOpen(true);
                                        }}
                                        onRemoveThreadFromFolder={
                                            removeThreadFromFolder
                                        }
                                        isDescendantOf={isDescendantOf}
                                        getThreadsForFolder={
                                            getThreadsForFolder
                                        }
                                        getThreadsForFolderIncludingSubfolders={
                                            getThreadsForFolderIncludingSubfolders
                                        }
                                        getFoldersInTreeOrder={
                                            getFoldersInTreeOrder
                                        }
                                        setOpenDropdownId={setOpenDropdownId}
                                        setOpenThreadDropdownId={
                                            setOpenThreadDropdownId
                                        }
                                    />
                                ))}

                                {/* Drop zone for moving subfolders to root - positioned at bottom to avoid content shift */}
                                {canMoveToRoot && showDropZone && (
                                    <SidebarMenuItem>
                                        <div
                                            className={cn(
                                                'mx-2 mt-3 mb-2 p-3 border-2 border-dashed rounded-lg transition-all duration-200',
                                                'flex items-center justify-center  text-muted-foreground',
                                                dragOverRoot
                                                    ? 'border-foreground scale-[1.02]'
                                                    : 'border-muted-foreground '
                                            )}
                                            data-root-drop="true"
                                            onDragOver={(e) => {
                                                if (
                                                    dragFolderId &&
                                                    canMoveToRoot
                                                ) {
                                                    e.preventDefault();
                                                    e.dataTransfer.dropEffect =
                                                        'move';
                                                    setDragOverRoot(true);
                                                }
                                            }}
                                            onDragLeave={(e) => {
                                                if (
                                                    !e.currentTarget.contains(
                                                        e.relatedTarget as Node
                                                    )
                                                ) {
                                                    setDragOverRoot(false);
                                                }
                                            }}
                                            onDrop={
                                                dragDropHandlers.handleRootDrop
                                            }
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">
                                                    Drop project here to move to
                                                    root level
                                                </span>
                                            </div>
                                        </div>
                                    </SidebarMenuItem>
                                )}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </div>

                {/* Time Groups */}
                <div
                    className={cn(
                        'mt-10 min-h-[200px] transition-all duration-200'
                    )}
                    data-time-groups="true"
                    onDragOver={dragDropHandlers.handleTimeGroupsDragOver}
                    onDragLeave={dragDropHandlers.handleTimeGroupsDragLeave}
                    onDrop={dragDropHandlers.handleTimeGroupsDrop}
                >
                    {/* <SidebarGroupLabel className="mt-6 mb-2 px-3 font-medium dark:text-slate-200 select-none">
                        Chats
                    </SidebarGroupLabel> */}

                    {/* Show drop zone hint when dragging a thread from a project */}
                    {((draggedThreadId &&
                        folderedThreadIds.has(draggedThreadId)) ||
                        (touchDraggedId &&
                            folderedThreadIds.has(touchDraggedId))) && (
                        <div
                            className={cn(
                                'mx-2 mb-4 px-2 py-4 border border-dashed rounded-md text-center transition-all duration-200',
                                dragOverTimeGroups ? 'bg-sidebar-accent' : ''
                            )}
                        >
                            ↓ Drop here to remove from project
                        </div>
                    )}
                    {/* Empty State */}

                    {!threadHistory ||
                    size(threadHistory?.timeGroupedThreads) === 0 ? (
                        <p className="m-3 text-muted-foreground">
                            <Translator path="threadHistory.sidebar.empty" />
                        </p>
                    ) : null}

                    <TimeGroupedThreadList
                        threadHistory={threadHistory!}
                        sortedTimeGroupKeys={sortedTimeGroupKeys}
                        folderedThreadIds={folderedThreadIds}
                        getTimeGroupLabel={getTimeGroupLabel}
                        onDragStart={
                            !isMobile
                                ? dragDropHandlers.handleDragStart
                                : undefined
                        }
                        onDragEnd={
                            !isMobile
                                ? dragDropHandlers.handleDragEnd
                                : undefined
                        }
                        onTouchStart={
                            isMobile
                                ? dragDropHandlers.handleTouchStart
                                : undefined
                        }
                        onTouchMove={
                            isMobile
                                ? dragDropHandlers.handleTouchMove
                                : undefined
                        }
                        onTouchEnd={
                            isMobile
                                ? dragDropHandlers.handleTouchEnd
                                : undefined
                        }
                        isMobile={isMobile}
                        isDragging={isDragging}
                        touchDraggedId={touchDraggedId}
                        onDeleteThread={setThreadIdToDelete}
                        onRenameThread={(threadId, name) => {
                            setThreadIdToRename(threadId);
                            setThreadNewName(name);
                        }}
                        onPinToOrg={userIsAdmin ? handlePinToOrg : undefined}
                        onShareThread={handleShareThread}
                        onAddToProject={handleAddToProject}
                        onCreateNewProject={(threadId) => {
                            if (threadId) {
                                setThreadToAssignAfterFolderCreation(threadId);
                            }
                            setAddFolderOpen(true);
                        }}
                        onRemoveFromProject={(threadId) => {
                            removeThreadFromFolder(threadId);
                        }}
                        folders={getFoldersInTreeOrder}
                        showTooltips={true}
                        showThreadOptions={true}
                        threadIdToRename={threadIdToRename}
                    />
                </div>
            </TooltipProvider>

            {isLoadingMore ? (
                <div className="flex justify-center items-center p-2">
                    <Loader />
                </div>
            ) : null}

            {/* Context Menu Portals */}
            {contextMenuPosition &&
                contextMenuTargetId &&
                createPortal(
                    <div
                        className="z-50 fixed"
                        style={{
                            left: `${contextMenuPosition.x}px`,
                            top: `${contextMenuPosition.y}px`
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {contextMenuType === 'thread' &&
                            (() => {
                                // Find the thread in all threads (including those in folders)
                                let thread = threadHistory?.threads?.find(
                                    (t) => t.id === contextMenuTargetId
                                );

                                // If not found in main threads, search in folder threads
                                if (!thread) {
                                    for (const folder of folders) {
                                        const folderThreads =
                                            getThreadsForFolder(folder.id);
                                        thread = folderThreads.find(
                                            (t) => t?.id === contextMenuTargetId
                                        );
                                        if (thread) break;
                                    }
                                }

                                if (!thread) return null;

                                // Check if thread is in any project by looking at assignments
                                const isThreadInProject = assignments.some(
                                    (assignment) =>
                                        assignment.threadId === thread.id
                                );

                                return (
                                    <ThreadContextMenu
                                        onDelete={() => {
                                            setThreadIdToDelete(thread.id);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onRename={() => {
                                            setThreadIdToRename(thread.id);
                                            setThreadNewName(thread.name);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onAddToProject={(folderId: string) => {
                                            removeThreadFromFolder(thread.id);
                                            assignThreadToFolder(
                                                folderId,
                                                thread.id
                                            );
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onCreateNewProject={() => {
                                            setThreadToAssignAfterFolderCreation(
                                                thread.id
                                            );
                                            setAddFolderOpen(true);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        folders={getFoldersInTreeOrder}
                                        isInProject={isThreadInProject}
                                        onRemoveFromProject={() => {
                                            removeThreadFromFolder(thread.id);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                    />
                                );
                            })()}

                        {contextMenuType === 'folder' &&
                            (() => {
                                const folder = folders.find(
                                    (f) => f.id === contextMenuTargetId
                                );
                                if (!folder) return null;

                                return (
                                    <FolderContextMenu
                                        folder={folder}
                                        folders={folders || []}
                                        onAddSubfolder={(parentId) => {
                                            setSubfolderParentId(parentId);
                                            setSubfolderName('');
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onAddChats={(folderId) => {
                                            openMultiSelectDialog(folderId);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onRename={(folderId, currentName) => {
                                            setFolderIdToRename(folderId);
                                            setFolderNewName(currentName);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onDelete={(folderId) => {
                                            setFolderIdToDelete(folderId);
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        onMove={(folderId, targetParentId) => {
                                            moveFolder(
                                                folderId,
                                                targetParentId
                                            );
                                            setContextMenuPosition(null);
                                            setContextMenuType(null);
                                            setContextMenuTargetId(null);
                                            setOpenThreadDropdownId(undefined);
                                            setOpenDropdownId(undefined);
                                        }}
                                        isDescendantOf={isDescendantOf}
                                    />
                                );
                            })()}
                    </div>,
                    document.body
                )}
        </>
    );
}
